import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { OptionsAnalyticsMetricInfoDialog } from './OptionsAnalyticsMetricInfoDialog';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export const OptionsPerformanceAccountSummaryCompact = ({ model }) => {
  const { isPortuguese } = useOptionsLocale();
  if (!model) return null;

  const labelTradingPnl = isPortuguese ? 'TRADING P&L (LÍQUIDO)' : 'TRADING P&L (NET)';
  const labelResultOnCapital = isPortuguese ? 'Resultado / Capital Inicial' : 'Result / Starting Capital';
  const labelDisclaimer = isPortuguese
    ? 'Indicador simples baseado no resultado realizado líquido e no capital inicial do período. Não ajusta depósitos ou saques ocorridos durante o período.'
    : 'Simple indicator based on realized result and starting capital for the period. It does not adjust for deposits or withdrawals.';
  const labelLink = isPortuguese ? 'Ver retorno por períodos →' : 'View return by capital periods →';

  const formatDateComplement = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    const formatted = d.toLocaleDateString(isPortuguese ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    return isPortuguese
      ? `Calculado desde ${formatted}, início da atividade de Options.`
      : `Calculated since ${formatted}, options trading start.`;
  };

  const handleScrollToCapitalPeriods = () => {
    const el = document.querySelector('[data-testid="options-capital-periods-section"]');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Card className="border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] text-white shadow-none" data-testid="options-performance-account-summary-card">
      <CardContent className="grid gap-5 px-4 py-4 sm:px-5 sm:py-5 xl:grid-cols-[1.1fr,1.05fr]" data-testid="options-performance-account-summary-content">
        <div className="space-y-4" data-testid="options-performance-account-summary-balance-side">
          <div className="space-y-2" data-testid="options-performance-account-summary-total">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/42" data-typography="label" data-testid="options-performance-account-summary-total-label">{model.totalLabel}</p>
            <p className="text-3xl font-black tracking-tight text-white sm:text-4xl" data-typography="metric" data-testid="options-performance-account-summary-total-value"><OptionsMonetaryText value={model.totalValue} iconSize={20} /></p>
            {model.totalSecondary ? <p className="text-sm text-white/55 tabular-nums" data-testid="options-performance-account-summary-total-secondary"><OptionsMonetaryText value={model.totalSecondary} iconSize={14} /></p> : null}
            {model.valuationNote ? <p className="text-sm text-[#FBBF24]" data-testid="options-performance-account-summary-total-valuation-note">{model.valuationNote}</p> : null}
          </div>

          <div className="space-y-2" data-testid="options-performance-account-summary-assets">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/42" data-typography="label" data-testid="options-performance-account-summary-assets-label">{model.assetsLabel}</p>
            <div className="rounded-[22px] border border-white/10 bg-black/20" data-testid="options-performance-account-summary-assets-list">
              {model.assets.map((asset, index) => (
                <div
                  key={asset.key}
                  className={`grid gap-2 px-4 py-3 md:grid-cols-[minmax(0,1.1fr),minmax(0,1fr),minmax(0,1fr)] md:items-center ${index > 0 ? 'border-t border-white/8' : ''}`}
                  data-testid={`options-performance-account-summary-asset-${asset.currency.toLowerCase()}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <OptionsAssetIcon assetKey={asset.currency} size={18} className="shrink-0" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-white" data-testid={`options-performance-account-summary-asset-name-${asset.currency.toLowerCase()}`}>{asset.name}</p>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-white/40" data-testid={`options-performance-account-summary-asset-code-${asset.currency.toLowerCase()}`}>{asset.currency}</p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-white md:text-right tabular-nums" data-testid={`options-performance-account-summary-asset-native-${asset.currency.toLowerCase()}`}><OptionsMonetaryText value={asset.nativeDisplay} iconSize={15} /></p>
                  <p className={`text-sm md:text-right tabular-nums ${asset.usdToneClass || 'text-white/60'}`} data-testid={`options-performance-account-summary-asset-usd-${asset.currency.toLowerCase()}`}>{asset.usdDisplay}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* LADO DIREITO: TRADING PERFORMANCE */}
        <div className="space-y-4" data-testid="options-performance-account-summary-trading-side">
          <div className="rounded-[22px] border border-white/10 bg-black/20 p-5 space-y-4" data-testid="options-performance-account-summary-trading-block">
            
            {/* TRADING P&L */}
            <div>
              <div className="flex items-center gap-1.5" data-testid="options-performance-account-summary-trading-label">
                <p className="text-[11px] uppercase tracking-[0.18em] text-white/40" data-typography="label">{labelTradingPnl}</p>
                {model.tradingInfo ? <OptionsAnalyticsMetricInfoDialog info={model.tradingInfo} testId="options-performance-account-summary-trading-label-info" /> : null}
              </div>
              <p className={`mt-2 text-3xl font-black tracking-tight sm:text-4xl ${model.tradingToneClass}`} data-typography="metric" data-testid="options-performance-account-summary-trading-value">
                <OptionsMonetaryText value={model.tradingValue} iconSize={20} />
              </p>
              {model.tradingSecondary ? (
                <p className="mt-1 text-sm text-white/55 tabular-nums" data-testid="options-performance-account-summary-trading-secondary">
                  <OptionsMonetaryText value={model.tradingSecondary} iconSize={14} />
                </p>
              ) : null}
            </div>

            {/* REALIZADO, NÃO REALIZADO, TAXAS */}
            <div className="grid gap-3 sm:grid-cols-3" data-testid="options-performance-account-summary-metrics-grid">
              <MiniMetric label={model.realizedLabel} info={model.realizedInfo} value={model.realizedValue} toneClass={model.realizedToneClass} testId="options-performance-account-summary-realized" />
              <MiniMetric label={model.unrealizedLabel} info={model.unrealizedInfo} value={model.unrealizedValue} toneClass={model.unrealizedToneClass} testId="options-performance-account-summary-unrealized" />
              <MiniMetric label={model.feesLabel} info={model.feesInfo} value={model.feesValue} secondary={model.feesSecondary} toneClass="text-white" testId="options-performance-account-summary-fees" />
            </div>

            {/* RESULTADO SOBRE CAPITAL INICIAL */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="options-performance-result-capital-block">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/40" data-typography="label">
                    {labelResultOnCapital}
                  </p>
                  <p className={`mt-1 text-xl font-bold tabular-nums ${model.resultOnStartingCapitalToneClass}`} data-testid="options-performance-result-capital-value">
                    {model.resultOnStartingCapitalFormatted}
                  </p>
                </div>

                {/* Botão de Scroll suave */}
                <button
                  type="button"
                  onClick={handleScrollToCapitalPeriods}
                  className="self-start text-xs font-semibold text-[#60A5FA] hover:text-[#93C5FD] transition-colors sm:self-center"
                  data-testid="options-performance-capital-periods-link"
                >
                  {labelLink}
                </button>
              </div>
              <div className="space-y-1" data-testid="options-performance-result-capital-disclaimer-container">
                <p className="text-[11px] text-white/45 leading-normal" data-testid="options-performance-result-capital-disclaimer">
                  {labelDisclaimer}
                </p>
                {model.isOperationalLimited && model.earliestExposureMs && (
                  <p className="text-[10px] text-white/35 font-semibold leading-normal" data-testid="options-performance-result-capital-operational-note">
                    {formatDateComplement(model.earliestExposureMs)}
                  </p>
                )}
              </div>
            </div>

          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MiniMetric = ({ label, info = null, value, secondary = null, toneClass = 'text-white', testId }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={testId}>
    <div className="flex items-center gap-1.5" data-testid={`${testId}-label`}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-typography="label">{label}</p>
      {info ? <OptionsAnalyticsMetricInfoDialog info={info} testId={`${testId}-info`} /> : null}
    </div>
    <p className={`mt-1 text-base font-bold tabular-nums ${toneClass}`} data-testid={`${testId}-value`}><OptionsMonetaryText value={value} iconSize={15} /></p>
    {secondary ? <p className="mt-1 text-xs text-white/52" data-testid={`${testId}-secondary`}><OptionsMonetaryText value={secondary} iconSize={13} /></p> : null}
  </div>
);

export default OptionsPerformanceAccountSummaryCompact;